import { Menu }                        from "@mantine/core";
import { Fragment, useEffect, useRef } from "react";

import { PrimaryButton }    from "_components/atoms/button";
import DonationTabEmptyView from "_components/atoms/donationTabEmptyView/container";
import FetchMore            from "_components/atoms/fetchMoreLoader/container";
import Icon                 from "_components/atoms/icon";
import Image                from "_components/atoms/image";
import Render               from "_components/atoms/render";
import Typography           from "_components/atoms/typography";
import UserComment          from "_components/features/chat/userComment";
import DonationGoalModal    from "_components/molecules/donationGoal";
import FanScoreCard         from "_components/molecules/fanScoreCard";

import useStyles from "./styles";

const footerData = {
	title              : "Total Donation Received",
	leaderBoardBtnText : "Back to Donations",
	btnText            : "See Top Donors"
};

const Component = ( {
	commentsList,
	totalDonationReceived,
	usersList,
	isDepleted,
	isFetching,
	isStreamer,
	donationGoalToggle,
	saveAndEnableDonationGoal,
	showDonationGoal,
	handleDonationGoalToggle,
	closeDonationGoalModal,
	donationGoalAmount,
	showLeaderBoard,
	toggleLeaderboardHandler,
	loadMore,
	streamerName,
	isBlockedByStreamer,
	chatPoppedOut,
	isSessionEnded
} ) => {
	const { classes } = useStyles ( { chatPoppedOut } );

	if ( !isStreamer && ( isBlockedByStreamer ) ) {
		const msg = "Donations will get featured here";

		return (
			<div className = { classes.donationEmptyViewContainer }>
				<DonationTabEmptyView msg = { msg } />
			</div>
		);
	}

	return (
		<div className = { classes.mainDonationTabContainer }>

			<div className = { classes.donationMainContentContainer }>
				<DonationGoalImageCard
					closeDonationGoalModal    = { closeDonationGoalModal }
					donationGoalAmount        = { donationGoalAmount }
					donationGoalToggle        = { donationGoalToggle }
					handleDonationGoalToggle  = { handleDonationGoalToggle }
					isStreamer                = { isStreamer }
					saveAndEnableDonationGoal = { saveAndEnableDonationGoal }
					showDonationGoal          = { showDonationGoal }
					streamerName              = { streamerName }
					totalDonationReceived     = { totalDonationReceived }
				/>

				<Render condition = { showLeaderBoard }>
					<div className = { classes.donationUsersContainer }>
						{
							usersList.map ( ( user, index ) => {

								return (
									<Fragment key = { `${ user.id }-${ index }` }>
										<FanScoreCard
											key                = { user.sportsFan.id }
											authorName         = { user.sportsFan.name }
											authorProfileImage = { user.sportsFan.photo }
											coins              = { user.donation }
											fanRank            = { user.rank }
											isSessionEnded     = { isSessionEnded }
											points             = { user.donation }
											profileHref        = { `/profile/${ user.sportsFan.id }` }
										/>
									</Fragment>
								);
							} )
						}

						<FetchMore
							fetchMore  = { loadMore }
							isDepleted = { isDepleted }
							isLoading  = { isFetching }
						/>
					</div>
				</Render>

				<Render condition = { !showLeaderBoard }>
					<RenderCommentList commentsList = { commentsList } />
				</Render>

			</div>

			<Render condition = { isStreamer }>
				<Footer
					btnLabel              = { showLeaderBoard ? footerData.leaderBoardBtnText : footerData.btnText }
					onClick               = { toggleLeaderboardHandler }
					totalDonationReceived = { totalDonationReceived }
				/>
			</Render>
		</div>
	);
};

export default Component;

const RenderCommentList = ( { commentsList } ) => {
	// these comments are related to donations made by audience
	//
	const divRef = useRef ( null );

	useEffect ( () => {
		const scroll = divRef.current.scrollHeight - divRef.current.clientHeight;

		divRef.current.scrollTo ( 0, scroll );

	}, [ commentsList.length ] );

	return (
		<div ref = { divRef }>
			{commentsList.map ( comment => (
				<UserComment
					key     = { comment.id }
					hideMenuOptionBtn
					showThanksBtn
					comment = { comment }
				/>
			)
			)}

		</div>
	);
};

const Footer = ( { onClick, btnLabel, totalDonationReceived } ) => {
	const { classes } = useStyles ( undefined, { name: "styles" } );

	return (
		<footer className = { classes.footerSection }>
			<div>
				<Typography
					className = { classes.footerTitle }
					color     = { "secondary" }
					title     = { footerData.title }
				/>

				<div className = { classes.donationAmountContainer }>
					<Icon sameInBothTheme
						className = { classes.diamondSvgInFooter }
						name      = "diamond"
					/>

					<Typography
						className = { `${ classes.diamondAmountText } fontBold` }
						color     = { "primary" }
						title     = { totalDonationReceived?.toLocaleString () }
						variant   = "body1"
					/>
				</div>
			</div>

			<PrimaryButton
				onClick = { onClick }
				title   = { btnLabel }
			/>

			{/* <Button className = { classes.seeTopDonorsBtn }
				color   = "secondary"
				onClick = { onClick }
				variant = "contained"
			>
				<Typography
					className = "fontBold capitalize"
					color     = { "textSecondary" }
					title     = { btnLabel }
					variant   = "body2"
				/>
			</Button> */}
		</footer>
	);
};

const DonationGoalImageCard = ( {
	totalDonationReceived,
	donationGoalAmount,
	streamerName,
	showDonationGoal,
	closeDonationGoalModal,
	saveAndEnableDonationGoal,
	isStreamer,
	handleDonationGoalToggle,
	donationGoalToggle
} ) => {

	const { classes } = useStyles ( undefined, { name: "styles" } );
	const percent     = ( totalDonationReceived / donationGoalAmount ) * 100;
	// const text    = `${ streamerName }’s Donation Goal (${ ( percent ).toFixed ( 0 ) }% Complete)`;
	const text = donationGoalAmount && percent < 100 ?
		`Help ${ streamerName } Achieve Donation Target. (${ ( percent ).toFixed ( 0 ) }% Complete)`
		: `${ streamerName }’s Total  Donation so far`;

	return (
		<div className = { classes.donationGoalImageContainer }>
			<Image src = "/images/donation-goal.webp" />

			<div className = { classes.textContainer }>
				<Typography
					className = { `${ classes.description } fontBold colorWhite` }
					color     = { "textSecondary" }
					size      = "xs"
					title     = { text }
					variant   = "body1"
				/>

				<div className = { classes.details }>
					<Icon sameInBothTheme
						className = { classes.diamondSvgIcon }
						name      = "diamond"
					/>

					<Typography
						className = { `${ classes.goalCount } fontBold colorWhite` }
						color     = { "textSecondary" }
						size      = "lg"
						title     = { totalDonationReceived?.toLocaleString () }
					/>

          &nbsp;

					<Render condition = { percent < 100 }>
						<Typography
							className = { `${ classes.donationReceived } fontBold colorWhite` }
							color     = { "textSecondary" }
							size      = "xs"
							title     = { " / " + donationGoalAmount?.toLocaleString () }
						/>
					</Render>

					<Render condition = { isStreamer }>
						<Menu withinPortal
							classNames      = { { dropdown: classes.menuDropdown } }
							onChange        = { () => {
								handleDonationGoalToggle ( { ...donationGoalToggle, status: !donationGoalToggle.status } );
							} }
							opened          = { showDonationGoal }
							position        = "bottom-end"
							radius          = "lg"
							transitionProps = { { duration: 100, transition: "pop-top-right" } }
						>

							<Menu.Target>
								<div>

									<Icon sameInBothTheme
										className = { classes.editIcon }
										name      = "donationTabEditIcon"
										onClick   = { e => {
											handleDonationGoalToggle ( { ...donationGoalToggle, status: !donationGoalToggle.status, e } );
										} }
									/>
								</div>
							</Menu.Target>

							{/* <Menu.Dropdown> */}
							<DonationGoalModal
								amountDescription    = "Based on your history, You should enter 💎1000 - 💎2000"
								featureAmount        = { donationGoalAmount }
								handleClose          = { closeDonationGoalModal }
								open                 = { !!( showDonationGoal ) }
								saveAndEnableFeature = { saveAndEnableDonationGoal }
								title                = "Enter Amount to Set as Goal"
								type                 = "donation"
							/>

							{/* </Menu.Dropdown> */}
						</Menu>
					</Render>
				</div>
			</div>

		</div>
	);
};
